"use client";

import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/components/ui/table";

import { Flex } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import defaultVisibilityState from "./data/defaultVisibilityState";
import DataTableToolBar from "./DataTableToolBar";
import { useSearchParams } from "next/navigation";
import { DataTablePagination } from "@/app/components/table/DataTablePagination";
import { PaginationState } from "@/lib/interface/PaginationState";
import AddConsignorButton from "./AddConsignorButton";
import ManageConsignorGroupsButton from "./ManageConsignorGroupsButton";
import ConsignorGroupContext from "@/lib/context/ConsignorGroupContext";
import useSWR from "swr";
import { ConsignorGroup } from "@prisma/client";
import { fetcher } from "@/lib/utils";
import { AugmentedConsignor } from "./columns";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

const REFETCH_INTERVAL = 15 * 1000;
export const CONSIGNOR_TABLE_COLUMN_VISIBILITY_CACHE_KEY =
  "consignorTableColumnVisibility";

export function DataTable<TData, TValue>({
  data,
  columns,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const searchParams = useSearchParams();
  const pageIndex = parseInt(searchParams.get("page") || "1");
  const pageSize = parseInt(searchParams.get("pageSize") || "10");

  const { data: consignorData } = useSWR("/api/v1/consignors",
    fetcher,
    {
      fallbackData: data,
      refreshInterval: REFETCH_INTERVAL
    });

  const { data: consignorGroupsData } = useSWR<ConsignorGroup[]>("/api/v1/consignor-groups",
    fetcher,
    {});

  const table = useReactTable<TData>({
    data: consignorData,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination: {
        pageIndex: pageIndex - 1,
        pageSize,
      },
    },
    filterFns: { missingImagesFilter: () => true },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const paginationState: PaginationState = {
    pageIndex,
    pageSize,
    pageCount: Math.ceil(table.getFilteredRowModel().rows.length / pageSize),
  };

  useEffect(() => {
    const cachedColumnVisibility =
      localStorage.getItem(CONSIGNOR_TABLE_COLUMN_VISIBILITY_CACHE_KEY) ?? null;
    if (
      cachedColumnVisibility !== null &&
      cachedColumnVisibility !== "undefined"
    ) {
      setColumnVisibility(JSON.parse(cachedColumnVisibility));
    } else {
      setColumnVisibility(defaultVisibilityState);
    }
  }, []);

  return (
    <ConsignorGroupContext.Provider value={{ consignorGroups: consignorGroupsData }}>
      <Flex direction="column" gap="4" className="w-full">
        <Flex justify="between" className="w-full">
          <DataTableToolBar table={table} />
          <Flex gap="2">
            {/* <ExportButton table={table} /> */}
            <ManageConsignorGroupsButton />
            <AddConsignorButton />
          </Flex>
        </Flex>
        <div className="rounded-md border bg-white">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row, id) => {
                  return (
                    <TableRow
                      key={id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <TableCell
                            key={cell.id}
                            style={{ width: cell.column.getSize() }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No consignors found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <DataTablePagination
          entityName="consignor"
          table={table}
          paginationState={paginationState}
        />
      </Flex>
    </ConsignorGroupContext.Provider>
  );
}
